/**
 * Modal to edit an existing organisation.
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { Check, Loader2, X } from 'lucide-react'
import type { OrganisationType } from '../../types'
import type { CreateOrgForm } from './types'

interface EditOrganisationModalProps {
  formData: CreateOrgForm
  setFormData: (data: CreateOrgForm) => void
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
}

const INPUT_CLASS =
  'w-full px-3 py-2 bg-white dark:bg-stone-900 border border-stone-300 dark:border-stone-600 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-[#4e5e82]'
const LABEL_CLASS = 'block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1'

function EditFields({
  formData,
  setFormData,
}: Pick<EditOrganisationModalProps, 'formData' | 'setFormData'>): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <>
      <div>
        <label className={LABEL_CLASS}>{t('organisations.fields.name')}</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => {
            setFormData({ ...formData, name: e.target.value })
          }}
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('organisations.fields.slug')}</label>
        <input
          type="text"
          disabled
          value={formData.slug}
          className="w-full rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-stone-500 dark:border-stone-600 dark:bg-stone-900"
        />
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          {t('organisations.slugCannotChange')}
        </p>
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('organisations.fields.type')}</label>
        <select
          value={formData.orgType}
          onChange={(e) => {
            setFormData({ ...formData, orgType: e.target.value as OrganisationType })
          }}
          className={INPUT_CLASS}
        >
          <option value="customer">{t('organisations.types.customer')}</option>
          <option value="client">{t('organisations.types.client')}</option>
        </select>
      </div>
      <div>
        <label className={LABEL_CLASS}>
          {t('organisations.fields.xeroOrganisationId')}
          <span className="ml-1 font-normal text-stone-400">({t('labels.optional')})</span>
        </label>
        <input
          type="text"
          value={formData.xeroOrganisationId}
          onChange={(e) => {
            setFormData({ ...formData, xeroOrganisationId: e.target.value })
          }}
          className={`${INPUT_CLASS} font-mono text-sm`}
        />
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          {t('organisations.xeroLinkHint')}
        </p>
      </div>
    </>
  )
}

function ModalFooter({
  formLoading,
  onClose,
}: Pick<EditOrganisationModalProps, 'formLoading' | 'onClose'>): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-4 py-2 text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={formLoading}
        className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        {t('actions.save')}
      </button>
    </div>
  )
}

export function EditOrganisationModal(props: EditOrganisationModalProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="flex items-center justify-between border-b border-stone-200 p-4 dark:border-stone-700">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {t('organisations.editOrganisation')}
          </h2>
          <button
            onClick={props.onClose}
            className="rounded p-1 hover:bg-stone-100 dark:hover:bg-stone-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={props.onSubmit} className="space-y-4 p-4">
          {props.formError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {props.formError}
            </div>
          )}
          <EditFields formData={props.formData} setFormData={props.setFormData} />
          <ModalFooter formLoading={props.formLoading} onClose={props.onClose} />
        </form>
      </div>
    </div>
  )
}
